"use client";

import { mobileLinks } from "@/data/navbar.data";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useId, useRef } from "react";
import { useIsClient } from "@/hooks/shared/useIsClient";
import { createPortal } from "react-dom";
import { IoMdMenu } from "react-icons/io";
import { IconType } from "react-icons";
import { TransitionLink } from "../shared/TransitionLink";
import { useDict } from "@/components/providers/LocaleProvider";
import { useNavMenu } from "./NavMenuContext";
import {
  NAVBAR,
  NAV_MOBILE_TOP_BAR_ID,
  navMenuBackdropClass,
  navMenuFromTopClass,
} from "./navbar.constants";

const SHUTTER_EASE = [0.65, 0, 0.35, 1] as const;
const SHUTTER_MS = 280;

interface MobileMenuProps {
  width: string;
}

interface MenuItemProps {
  href: string;
  name: string;
  classNameForIcon: string;
  Icon: IconType;
  onNavigate: () => void;
}

const MobileMenu = ({ width }: MobileMenuProps) => {
  const t = useDict();
  const menuId = useId();
  const { isOpen, toggle, close } = useNavMenu();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const mounted = useIsClient();

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      const topBar = document.getElementById(NAV_MOBILE_TOP_BAR_ID);
      if (
        panelRef.current?.contains(target) ||
        triggerRef.current?.contains(target) ||
        topBar?.contains(target)
      ) {
        return;
      }
      close();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, close]);

  const menuPortal =
    mounted &&
    createPortal(
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.button
              type="button"
              key="mobile-menu-backdrop"
              aria-label="Close menu"
              onClick={close}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={`fixed inset-x-0 ${navMenuBackdropClass} ${NAVBAR.zIndex.menuBackdrop} bg-black/50 lg:hidden`}
            />

            <motion.section
              ref={panelRef}
              key="mobile-menu-shutter"
              id={menuId}
              role="dialog"
              aria-modal="true"
              aria-label={t.nav.menu}
              initial={{ scaleY: 0, opacity: 0 }}
              animate={{ scaleY: 1, opacity: 1 }}
              exit={{ scaleY: 0, opacity: 0 }}
              transition={{ duration: SHUTTER_MS / 1000, ease: SHUTTER_EASE }}
              style={{ transformOrigin: "top center" }}
              className={`fixed inset-x-0 ${navMenuFromTopClass} ${NAVBAR.zIndex.menuShutter} lg:hidden overflow-hidden bg-background shadow-lg`}
            >
              <ul className="pt-[var(--nav-mobile-top)] max-h-[calc(100dvh-var(--nav-mobile-bottom))] overflow-y-auto no-scrollbar">
                {mobileLinks.map((link) => (
                  <MenuItem
                    key={link.id}
                    href={link.href}
                    name={t.nav.links[link.id]}
                    Icon={link.Icon}
                    classNameForIcon={link.classNameForIcon}
                    onNavigate={close}
                  />
                ))}
              </ul>
            </motion.section>
          </>
        )}
      </AnimatePresence>,
      document.body
    );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        style={{ width, textAlign: "center" }}
        className="lg:hidden flex flex-col justify-center items-center cursor-pointer hover:text-black gap-[2px] rounded-full transition-colors duration-300 z-10"
        aria-expanded={isOpen}
        aria-controls={menuId}
      >
        <IoMdMenu size={16} aria-hidden />
        <span className="text-[10px] lg:text-base">{t.nav.menu}</span>
      </button>

      {menuPortal}
    </>
  );
};

function MenuItem({
  href,
  name,
  classNameForIcon,
  Icon,
  onNavigate,
}: MenuItemProps) {
  return (
    <li className="py-3 text-center gap-2 border-b flex justify-center items-center border-light-border last:border-b-0">
      <div>{Icon && <Icon className={classNameForIcon} aria-hidden />}</div>
      <div className="min-w-[110px] w-[110px] place-self-center flex items-center gap-x-2">
        <TransitionLink
          href={href}
          onClick={onNavigate}
          className="hover:text-primary"
        >
          {name}
        </TransitionLink>
      </div>
    </li>
  );
}

export default MobileMenu;
